import { useCallback, useEffect, useState } from "react";
import api from "api/api";
import AuthService from "lib/Auth";

// Props Validations
import PropTypes from "prop-types";

// import components
import Header from "../components/common/Header";

// mui components
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

// card images
import deodorantsImage from "../assets/deodorants.png";
import oralCareImage from "../assets/oralCare.png";
import skinCleansingImage from "../assets/skinCleansing.png";
import hairCareImage from "../assets/hairCare.png"; // Add Hair Care image
import skinCareImage from "../assets/skinCare.png"; // Add Skin Care image
import unileverFragranceHouseImage from "../assets/unileverFragranceHouse.png"; // Add Unilever Fragrance House image

// redux
import { useSelector, useDispatch } from "react-redux";
import { loadProductCategoryState, loadBusinessGroupState } from "store/features/settingsSlice";
import { loadRoleState, loadUserAdminState, loadIdState } from "store/features/userSlice";

import { resetStudyDataStoreState } from "store/features/studyDataSlice";
import { resetProductsInfoStore } from "store/features/productsInfoSlice";
import { resetDriverAnalysisStore } from "store/features/driverAnalysisSlice";
import { resetDataMappingStoreState } from "store/features/dataMappingSlice";
import { resetCorrelationStore } from "store/features/correlationSlice";
import { resetConsumerFilterStoreState } from "store/features/consumerFilterSlice";
import { resetClaimsMappingStore } from "store/features/claimsMappingSlice";
import { resetClaimsAnalysisStore } from "store/features/claimsAnalysisSlice";
import { resetAnalysisResultsStoreState } from "store/features/analysisResultsSlice";
import { resetRespondentDashboardStoreState } from "store/features/respondentDashboardSlice";
import { resetAnalysisKpiStoreState } from "store/features/analysisKpiSlice";
import { showNotification } from "store/features/notificationSlice";
import { resetMatchNetStoreState } from "store/features/matchNetSlice";

// react router dom
import { useNavigate } from "react-router-dom";

const HOME_SELECTION_KEY = "deohut_home_selection";

const categoryImages = {
    deodorants: deodorantsImage,
    "oral care": oralCareImage,
    "skin cleansing": skinCleansingImage,
    "hair care": hairCareImage,
    "skin care": skinCareImage,
    "unilever fragrance house": unileverFragranceHouseImage,
};

function Home({ headerTitle }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [businessGroups, setBusinessGroups] = useState([]);
    const [productCategoryOptions, setProductCategoryOptions] = useState([]);
    const [selectedTab, setSelectedTab] = useState(0);

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    const businessGroupData = useSelector((state) => state.settingsStore.businessGroup);

    const resetStore = useCallback(() => {
        dispatch(resetStudyDataStoreState());
        dispatch(resetProductsInfoStore());
        dispatch(resetDriverAnalysisStore());
        dispatch(resetDataMappingStoreState());
        dispatch(resetCorrelationStore());
        dispatch(resetConsumerFilterStoreState());
        dispatch(resetClaimsMappingStore());
        dispatch(resetClaimsAnalysisStore());
        dispatch(resetAnalysisResultsStoreState());
        dispatch(resetRespondentDashboardStoreState());
        dispatch(resetAnalysisKpiStoreState());
        dispatch(resetMatchNetStoreState());
    }, [dispatch]);

    // Fetch product categories for a specific business group
    const fetchProductCategoryData = useCallback(
        (businessGroupId, savedCategory = null) => {
            try {
                const url = process.env.REACT_APP_SERVER_PATH + `/product-categories/all/?archive=False&business_group=${businessGroupId}`;
                api.get(url)
                    .then(async (res) => {
                        const categories = res.data?.map((datum) => {
                            return {
                                value: datum.id,
                                label: datum.title,
                            };
                        });
                        setProductCategoryOptions(categories);
                        if (savedCategory && Object.keys(savedCategory).length > 0) {
                            const match = categories.find((c) => c.value === savedCategory.value);
                            if (match) {
                                dispatch(loadProductCategoryState(match));
                            }
                        }
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                    });
            } catch (error) {
                handleError("error", error);
            }
        },
        [dispatch]
    );

    // Fetch business groups
    const fetchBusinessGroups = useCallback(() => {
        try {
            const url = process.env.REACT_APP_SERVER_PATH + `/business-groups/all/?archive=False`;
            api.get(url)
                .then(async (res) => {
                    let defaultTab = 0;
                    const businessGroupsData = res.data?.map((datum, index) => {
                        const defaultBussinessGroupTitle = process.env?.REACT_APP_PERSONAL_CARE?.toLowerCase();
                        if (defaultBussinessGroupTitle && datum.title.toLowerCase() === defaultBussinessGroupTitle) {
                            defaultTab = index;
                        }
                        return {
                            id: datum.id,
                            title: datum.title,
                        };
                    });
                    setBusinessGroups(businessGroupsData);

                    if (businessGroupsData.length > 0) {
                        // Check localStorage for a previously saved selection
                        const savedRaw = localStorage.getItem(HOME_SELECTION_KEY);
                        const saved = savedRaw ? JSON.parse(savedRaw) : null;

                        let targetTab = defaultTab;
                        let targetBusinessGroup = {
                            value: businessGroupsData[defaultTab].id,
                            label: businessGroupsData[defaultTab].title,
                        };
                        let savedCategory = null;

                        if (saved?.businessGroup?.value) {
                            const savedIndex = businessGroupsData.findIndex((bg) => bg.id === saved.businessGroup.value);
                            if (savedIndex !== -1) {
                                targetTab = savedIndex;
                                targetBusinessGroup = saved.businessGroup;
                                savedCategory = saved.productCategory || null;
                            }
                        }

                        setSelectedTab(targetTab);
                        dispatch(loadBusinessGroupState(targetBusinessGroup));
                        fetchProductCategoryData(businessGroupsData[targetTab].id, savedCategory);
                    }
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [dispatch, fetchProductCategoryData]); // ← Add dispatch to dependencies

    useEffect(() => {
        fetchBusinessGroups();
        resetStore();
    }, [resetStore, fetchBusinessGroups]);

    // temporary user fetch until auth context is implemented
    useEffect(() => {
        const fetchUser = (retry = false) => {
            let url = `${process.env.REACT_APP_SERVER_PATH}/profiles/me/`;
            if (productCategoryId) {
                url += `?product_category=${productCategoryId}`;
            }
            api.get(url, AuthService.getAuthHeader())
                .then((res) => {
                    dispatch(loadRoleState(res?.data?.role || null));
                    dispatch(loadUserAdminState(res?.data?.user_admin || false));
                    dispatch(loadIdState(res?.data?.id || null));
                })
                .catch((error) => {
                    if (!retry) {
                        AuthService.refreshToken().then(() => fetchUser(true));
                    } else {
                        console.error("Authentication failed", error);
                    }
                })
                .finally();
        };

        fetchUser();
    }, [dispatch, productCategoryId]);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        // Clear selected category when switching tabs
        dispatch(loadProductCategoryState({}));

        // Fetch product categories for the selected business group
        if (businessGroups[newValue]) {
            // Store selected business group in Redux
            const selectedBusinessGroup = {
                value: businessGroups[newValue].id,
                label: businessGroups[newValue].title,
            };
            dispatch(loadBusinessGroupState(selectedBusinessGroup));

            fetchProductCategoryData(businessGroups[newValue].id);
        }
    };

    const updateProductCategory = (datum) => {
        dispatch(loadProductCategoryState(datum));
        localStorage.setItem(HOME_SELECTION_KEY, JSON.stringify({ businessGroup: businessGroupData, productCategory: datum }));
    };

    const handleSubmit = () => {
        navigate("/studies");
    };

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    return (
        <div>
            <Header title={headerTitle} />
            <div
                className="background-color-white  box-shadow--full-container"
                style={{
                    minHeight: "calc(100vh - 140px)",
                    margin: "40px",
                    borderRadius: "10px",
                }}
            >
                <div>
                    {businessGroups.length > 0 && (
                        <Box sx={{ width: "100%", paddingTop: "20px" }}>
                            <Tabs
                                value={selectedTab}
                                onChange={handleTabChange}
                                centered
                                sx={{
                                    "& .MuiTabs-indicator": {
                                        display: "none",
                                    },
                                    "& .MuiTab-root": {
                                        fontSize: "18px",
                                    },
                                }}
                                className="tabs-container tabs-toggle"
                            >
                                {businessGroups.map((businessGroup, index) => (
                                    <Tab
                                        key={businessGroup.id}
                                        className="tab-labels"
                                        label={businessGroup.title}
                                        value={index}
                                    />
                                ))}
                            </Tabs>
                        </Box>
                    )}
                </div>
                <div
                    style={{
                        minHeight: "calc(100vh - 220px)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <div>
                        <div className="boxed d-flex justify-content-center align-items-center">
                            {productCategoryOptions.map((datum) => {
                                return (
                                    <div key={datum.value}>
                                        <input
                                            type="radio"
                                            id={datum.label}
                                            value={datum.label}
                                            checked={datum.label === productCategoryData.label}
                                            name="product_category"
                                        />
                                        <label
                                            onClick={() => updateProductCategory(datum)}
                                            htmlFor={datum.label}
                                            className="button"
                                        >
                                            <img
                                                src={categoryImages[datum.label.toLowerCase()]}
                                                alt={datum.label}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    marginBottom: "7px",
                                                }}
                                            />
                                            {datum.label}
                                            <br />
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="text-center">
                            <Button
                                style={{
                                    width: "240px",
                                    fontSize: "18px",
                                }}
                                size="small"
                                type="submit"
                                className={Object.keys(productCategoryData).length === 0 ? "custom-btn box-shadow--3" : "custom-btn liner-light-blue-background"}
                                variant="contained"
                                disabled={Object.keys(productCategoryData).length === 0}
                                onClick={handleSubmit}
                            >
                                Confirm
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Typechecking props for the Home
Home.propTypes = {
    headerTitle: PropTypes.string,
};

export default Home;
